import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { TaskInstance } from 'ember-concurrency'
import { didCancel } from 'ember-concurrency'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { IntlService } from 'ember-intl'
import { action } from '@ember/object'
import type { Err, Ok } from 'neverthrow'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { tracked } from '@glimmer/tracking'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type StudentCsvMaker from 'teacher-ui-client/services/student-csv-maker'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { LayoutBlocksConfirmModal } from 'teacher-ui-client/components/layout-blocks/confirm-modal'
import { t } from 'ember-intl'
import { LinkTo } from '@ember/routing'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'

export class RecentRolloverPrompt extends Component {
  @service studentCsvMaker!: StudentCsvMaker

  @service subscriptionType!: SubscriptionTypeService

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service schoolRollover!: SchoolRolloverService

  @tracked
  modalDismissed = false

  @tracked
  downloadTaskInstance?: TaskInstance<
    | Ok<
        {
          studentCount: number
        },
        never
      >
    | Err<
        never,
        {
          error: unknown
          studentCount: number
        }
      >
  >

  get showModal() {
    const notDismissed = !this.modalDismissed
    const noActiveRollover = this.schoolRollover.activeRollovers.length === 0
    const hasLatestRecentOtherRollover =
      this.schoolRollover.latestRecentRollover?.subscriptionType === this.otherSubscriptionType
    return notDismissed && noActiveRollover && hasLatestRecentOtherRollover
  }

  get isDownloadingCsv() {
    return this.downloadTaskInstance?.isRunning
  }

  get currentSubscriptionType() {
    return this.subscriptionType.current
  }

  /**
   * From the context of only Reading or Maths rollovers - Writing doesn't partake in these structured rollovers, so we
   * ignore it.
   */
  get otherSubscriptionType(): SubscriptionType.Reading | SubscriptionType.Maths {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return SubscriptionType.Maths
      case SubscriptionType.Maths:
        return SubscriptionType.Reading
      default:
        return SubscriptionType.Reading
    }
  }

  @action
  dismissModal() {
    this.modalDismissed = true
  }

  @action
  async downloadCsv() {
    const filename = this.intl.t('manage.rollover.steps.students.export.filename')
    try {
      this.downloadTaskInstance = this.studentCsvMaker.downloadRolloverCsv.perform(filename)
      const result = await this.downloadTaskInstance
      if (result.isOk()) {
        this.showSuccessMessage(result.value.studentCount)
      } else {
        this.showFailMessage()
      }
    } catch (error: unknown) {
      if (!didCancel(error)) {
        this.showFailMessage()
      }
    }
  }

  private showSuccessMessage(studentCount: number) {
    const successMessage = this.intl.t('manage.manageClasses.students.bulkActions.exportStudents.success', {
      count: studentCount,
    })
    this.flashQueue.addSuccess({ subtitle: successMessage })
  }

  private showFailMessage() {
    const errorMessage = this.intl.t('manage.manageClasses.students.bulkActions.exportStudents.error')
    this.flashQueue.addFail({ subtitle: errorMessage })
  }

  <template>
    <LayoutBlocksConfirmModal
      data-test-recent-rollover-prompt
      class="mx-4"
      @title={{t "manage.rollover.recentRolloverPrompt.title"}}
      @showModal={{this.showModal}}
      @cancel={{this.dismissModal}}
      @cancelButtonText={{t "cancel"}}
      @confirm={{this.dismissModal}}
      @confirmButtonText={{t "manage.rollover.recentRolloverPrompt.continue"}}
      @isSubmitting={{this.isDownloadingCsv}}
    >
      <div class="grid grid-cols-[1fr_max-content] gap-y-4">
        {{#if this.schoolRollover.latestRecentRollover.subscriptionType}}
          <p>
            {{t
              "manage.rollover.recentRolloverPrompt.intro"
              subscriptionType=(t this.schoolRollover.latestRecentRollover.subscriptionType)
            }}
          </p>
        {{/if}}
        <div></div>
        <div class="space-y-4 border-y py-4">
          <h5>
            {{t "manage.rollover.recentRolloverPrompt.exportHeading"}}
          </h5>
          <p>
            {{t "manage.rollover.recentRolloverPrompt.exportText"}}
          </p>
        </div>
        <div class="flex items-center border-y">
          <UiButton
            data-test-download-students-csv
            @disabled={{this.isDownloadingCsv}}
            {{on "click" this.downloadCsv}}
            class="ml-12 w-32 whitespace-nowrap"
          >
            <PhosphorIcon class="pr-2" @icon="phosphor-download-fill" />{{t
              "manage.rollover.recentRolloverPrompt.exportButton"
            }}
          </UiButton>
        </div>
        <div class="space-y-4">
          <h5>
            {{t "manage.rollover.recentRolloverPrompt.copySchoolClassesHeading"}}
          </h5>
          <p>
            {{t
              "manage.rollover.recentRolloverPrompt.copySchoolClassesText"
              oppositeSubscriptionType=(t this.otherSubscriptionType)
              reading=(t "reading")
              maths=(t "maths")
            }}
            <LinkTo class="font-bold underline visited:font-bold" @route="manage.students">{{t
                "manage.title.students"
              }}</LinkTo>.
          </p>
        </div>
        <div></div>
      </div>
    </LayoutBlocksConfirmModal>
  </template>
}

export default RecentRolloverPrompt
